/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  Button
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";

export default function ImportProgress({ data, onDismiss }) {
  if (!data) return null;

  // Note: invalidExernalSource / invalidMaketPlace are intentionally spelled to
  // match the (typo'd) field names returned by the import API.
  const {
    summary,
    invalidProducts,
    invalidTaxCodes,
    invalidExernalSource,
    invalidMaketPlace,
    statusMessage,
  } = data;

  let progressColor = "success";
  if (statusMessage.includes("Failed")) progressColor = "error";
  else if (statusMessage.includes("warnings")) progressColor = "warning";

  return (
    <Box sx={{ p: 1.5, border: "1px solid #ddd", borderRadius: 1, mt: 1 }}>
      {/* Header */}
      <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
        {statusMessage}
      </Typography>

      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={100}
        sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
        color={progressColor}
      />

      {/* Summary Badges */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${summary.updatedCount} updated`}
          color="success"
          size="small"
        />

        {summary.skippedCount > 0 && (
          <Chip
            icon={<WarningIcon />}
            label={`${summary.skippedCount} skipped`}
            color="warning"
            size="small"
          />
        )}

        {summary.updatedCount === 0 && (
          <Chip icon={<ErrorIcon />} label="Import Failed" color="error" size="small" />
        )}

        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={onDismiss}
          sx={{ fontSize: "0.75rem" }}
        >
          Dismiss
        </Button>
      </Stack>

      {/* Invalid Products */}
      {invalidProducts?.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          <WarningIcon color="warning" sx={{ fontSize: 14, verticalAlign: "middle" }} />
          <Typography variant="caption">
            <strong>Invalid Customers:</strong> {invalidProducts.join(", ")}
          </Typography>
        </Stack>
      )}

      {/* Invalid Tax Codes */}
      {invalidTaxCodes?.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <WarningIcon color="warning" sx={{ fontSize: 14, verticalAlign: "middle" }} />
          <Typography variant="caption">
            <strong>Invalid Zamp Entities:</strong> {invalidTaxCodes.join(", ")}
          </Typography>
        </Stack>
      )}

      {/* Invalid External Source */}
      {invalidExernalSource?.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <WarningIcon color="warning" sx={{ fontSize: 14, verticalAlign: "middle" }} />
          <Typography variant="caption">
            <strong>Invalid External Source:</strong> {invalidExernalSource.join(", ")}
          </Typography>
        </Stack>
      )}

      {/* Invalid Marketplace */}
      {invalidMaketPlace?.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <WarningIcon color="warning" sx={{ fontSize: 14, verticalAlign: "middle" }} />
          <Typography variant="caption">
            <strong>Invalid Marketplace:</strong> {invalidMaketPlace.join(", ")}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
